/**
 * Persists the last visited module route on TotalAccessOfflineDB (`app-shell` domain)
 * so diagnostics can recover module context without relying on legacy localStorage keys.
 */
import appShellStore from './appShellStore';

const LAST_VISITED_MODULE_ENTITY_ID = 'last_visited_module_context';

export interface PersistedLastVisitedModulePayload extends Record<string, unknown> {
  route: string;
  module?: string | null;
  cached_at: string;
}

function parsePositiveInteger(value: string | null | undefined): number {
  if (!value) {
    return 0;
  }

  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function getPersistedLastVisitedModuleClientId(): number {
  if (typeof window === 'undefined') {
    return 0;
  }

  try {
    const currentClientId = parsePositiveInteger(window.localStorage.getItem('current_client_id'));
    if (currentClientId) {
      return currentClientId;
    }

    const rawUserData = window.localStorage.getItem('user_data');
    if (!rawUserData) {
      return 0;
    }

    const parsed = JSON.parse(rawUserData) as {
      client_id?: number | string;
      clientId?: number | string;
    };

    return parsePositiveInteger(String(parsed.client_id ?? parsed.clientId ?? ''));
  } catch {
    return 0;
  }
}

export async function readPersistedLastVisitedModule(
  clientId: number = getPersistedLastVisitedModuleClientId(),
): Promise<PersistedLastVisitedModulePayload | undefined> {
  if (!clientId) {
    return undefined;
  }

  const cached = await appShellStore.getJson<PersistedLastVisitedModulePayload>(
    clientId,
    LAST_VISITED_MODULE_ENTITY_ID,
  );

  return cached ?? undefined;
}

export async function writePersistedLastVisitedModule(
  route: string,
  module: string | null,
  clientId: number = getPersistedLastVisitedModuleClientId(),
): Promise<void> {
  if (!clientId || !route) {
    return;
  }

  await appShellStore.upsertJson(clientId, LAST_VISITED_MODULE_ENTITY_ID, {
    route,
    module,
    cached_at: new Date().toISOString(),
  });

  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem('lastVisitedModule');
    } catch {
      /* ignore legacy cleanup failures */
    }
  }
}